import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, GraduationCap } from "lucide-react";
import api, { formatRupiah, formatTanggal, BULAN } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function ParentHome() {
  const [data, setData] = useState(null);
  const [savings, setSavings] = useState([]);
  const [bills, setBills] = useState([]);

  useEffect(() => {
    api.get("/spp/my").then((r) => setData(r.data)).catch(() => setData({ children: [] }));
    api.get("/savings/my").then((r) => setSavings(r.data)).catch(() => {});
    api.get("/bills/my/list").then((r) => setBills(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="py-20 text-center text-sm text-slate-500" data-testid="parent-loading">Memuat data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="parent-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Anak Saya</h1>
        <p className="mt-1 text-sm text-slate-500">
          {data.school_name} — Status SPP periode {BULAN[data.month - 1]} {data.year}
        </p>
      </div>

      {data.children.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="parent-no-children">
          <GraduationCap className="mx-auto mb-3 h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <p className="font-heading text-lg font-semibold text-slate-800">Belum ada anak yang tertaut</p>
          <p className="mt-1 text-sm text-slate-500">Hubungi bendahara sekolah untuk menautkan akun Anda ke data anak.</p>
        </div>
      )}

      {data.children.map((c) => (
        <div key={c.student.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`child-card-${c.student.nisn}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-900">{c.student.name}</h2>
              <p className="text-sm text-slate-500">Kelas {c.student.class_name} · NISN {c.student.nisn}</p>
            </div>
            <div className="text-right">
              {c.status === "lunas" && (
                <Badge className="gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-600 hover:bg-emerald-50" data-testid={`child-status-lunas-${c.student.nisn}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> SPP Bulan Ini Lunas
                </Badge>
              )}
              {c.status === "sebagian" && (
                <Badge className="gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-amber-600 hover:bg-amber-50" data-testid={`child-status-sebagian-${c.student.nisn}`}>
                  <AlertCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Terbayar {c.percent}% ({formatRupiah(c.total_paid)})
                </Badge>
              )}
              {c.status === "belum" && (
                <Badge className="gap-1.5 rounded-full bg-rose-50 px-3 py-1.5 text-rose-600 hover:bg-rose-50" data-testid={`child-status-belum-${c.student.nisn}`}>
                  <AlertCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> SPP Bulan Ini Belum Dibayar
                </Badge>
              )}
              <p className="mt-1.5 text-xs text-slate-500">Nominal: {formatRupiah(data.spp_nominal)}/bulan</p>
            </div>
          </div>

          <div className="mt-5">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Riwayat Pembayaran</h3>
            <div className="overflow-x-auto">
              <Table data-testid={`child-history-table-${c.student.nisn}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Periode</TableHead>
                    <TableHead>Tanggal Bayar</TableHead>
                    <TableHead>Metode</TableHead>
                    <TableHead className="text-right">Jumlah</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {c.history.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-slate-500">Belum ada riwayat pembayaran.</TableCell></TableRow>
                  )}
                  {c.history.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm text-slate-700">{BULAN[p.month - 1]} {p.year}</TableCell>
                      <TableCell className="text-sm text-slate-600">{formatTanggal(p.paid_at)}</TableCell>
                      <TableCell className="text-sm text-slate-600">{p.method}</TableCell>
                      <TableCell className="text-right text-sm font-medium text-slate-900">{formatRupiah(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {(() => {
            const sv = savings.find((x) => x.student.id === c.student.id);
            if (!sv) return null;
            return (
              <div className="mt-6 border-t border-slate-100 pt-5" data-testid={`child-savings-${c.student.nisn}`}>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Tabungan — Saldo <span className="text-slate-800">{formatRupiah(sv.balance)}</span>
                </h3>
                <div className="overflow-x-auto">
                  <Table data-testid={`child-savings-table-${c.student.nisn}`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tanggal</TableHead>
                        <TableHead>Jenis</TableHead>
                        <TableHead className="text-right">Jumlah</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sv.history.length === 0 && (
                        <TableRow><TableCell colSpan={4} className="py-4 text-center text-sm text-slate-500">Belum ada transaksi tabungan.</TableCell></TableRow>
                      )}
                      {sv.history.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell className="text-sm text-slate-600">{formatTanggal(t.created_at)}</TableCell>
                          <TableCell>
                            {t.type === "setor" ? (
                              <Badge className="rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-50">Setor</Badge>
                            ) : (
                              <Badge className="rounded-full bg-amber-50 text-amber-600 hover:bg-amber-50">Tarik</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium text-slate-900">{formatRupiah(t.amount)}</TableCell>
                          <TableCell className="text-right text-sm text-slate-600">{formatRupiah(t.balance_after)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          })()}

          {(() => {
            const bl = bills.find((x) => x.student.id === c.student.id);
            if (!bl || bl.bills.length === 0) return null;
            return (
              <div className="mt-6 border-t border-slate-100 pt-5" data-testid={`child-bills-${c.student.nisn}`}>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Tagihan Lainnya</h3>
                <div className="space-y-2">
                  {bl.bills.map((item) => (
                    <div key={item.bill.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`child-bill-${item.bill.id}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{item.bill.title}</p>
                        <p className="text-xs text-slate-500">
                          {formatRupiah(item.bill.amount)}
                          {item.bill.due_date ? ` · Jatuh tempo ${item.bill.due_date}` : ""}
                          {item.total_paid > 0 ? ` · Terbayar ${formatRupiah(item.total_paid)}` : ""}
                        </p>
                      </div>
                      {item.status === "lunas" && (
                        <Badge className="rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-50">Lunas</Badge>
                      )}
                      {item.status === "sebagian" && (
                        <Badge className="rounded-full bg-amber-50 text-amber-600 hover:bg-amber-50">Sebagian {item.percent}%</Badge>
                      )}
                      {item.status === "belum" && (
                        <Badge className="rounded-full bg-rose-50 text-rose-600 hover:bg-rose-50">Belum</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      ))}
    </div>
  );
}
